import React, { useState, useMemo } from 'react';
import {
  FaArrowLeft,
  FaStar,
  FaCalendarAlt,
  FaWater,
  FaHotel,
  FaUtensils,
  FaMapMarkerAlt,
  FaInfoCircle,
  FaChevronRight,
  FaSearch,
  FaFilter,
  FaThLarge,
  FaList,
  FaChevronLeft,
  FaChevronRight as FaChevronRightIcon,
} from 'react-icons/fa';
import { FaLocationDot } from 'react-icons/fa6';
import PackageCard from '../components/PackageCard';

const FullScreenPackages = ({ 
  isOpen, 
  onClose, 
  packages = [], 
  onShowItem,
  title = "All Packages",
  subtitle = "Discover our complete collection of dive packages"
}) => {
  const [searchTerm, setSearchTerm] = useState('');
  const [filterBy, setFilterBy] = useState('all');
  const [sortBy, setSortBy] = useState('default');
  const [viewMode, setViewMode] = useState('grid');
  const [currentPage, setCurrentPage] = useState(1);
  const itemsPerPage = 8;

  // Get unique categories from packages
  const categories = useMemo(() => {
    const cats = new Set();
    packages.forEach(pkg => {
      if (pkg.category) cats.add(pkg.category);
    });
    return ['all', ...Array.from(cats)];
  }, [packages]);

  // Filter and sort packages
  const filteredPackages = useMemo(() => {
    let filtered = [...packages];

    if (searchTerm) {
      filtered = filtered.filter(pkg =>
        pkg.name?.toLowerCase().includes(searchTerm.toLowerCase()) ||
        pkg.label?.toLowerCase().includes(searchTerm.toLowerCase()) ||
        pkg.description?.toLowerCase().includes(searchTerm.toLowerCase())
      );
    }

    if (filterBy !== 'all') {
      filtered = filtered.filter(pkg => pkg.category === filterBy);
    }

    switch (sortBy) {
      case 'price-low':
        filtered.sort((a, b) => {
          const priceA = a.onImageButtons?.[0]?.value?.replace(/[^0-9.]/g, '') || 0;
          const priceB = b.onImageButtons?.[0]?.value?.replace(/[^0-9.]/g, '') || 0;
          return parseFloat(priceA) - parseFloat(priceB);
        });
        break;
      case 'price-high':
        filtered.sort((a, b) => {
          const priceA = a.onImageButtons?.[0]?.value?.replace(/[^0-9.]/g, '') || 0;
          const priceB = b.onImageButtons?.[0]?.value?.replace(/[^0-9.]/g, '') || 0;
          return parseFloat(priceB) - parseFloat(priceA);
        });
        break;
      case 'rating':
        filtered.sort((a, b) => (b.rating || 0) - (a.rating || 0));
        break;
      case 'name':
        filtered.sort((a, b) => (a.name || '').localeCompare(b.name || ''));
        break;
      default:
        break;
    }

    return filtered;
  }, [packages, searchTerm, filterBy, sortBy]);

  const totalPages = Math.ceil(filteredPackages.length / itemsPerPage);
  const paginatedPackages = filteredPackages.slice(
    (currentPage - 1) * itemsPerPage,
    currentPage * itemsPerPage
  );

  const handlePageChange = (page) => {
    setCurrentPage(page);
    window.scrollTo({ top: 0, behavior: 'smooth' });
  };

  const handleShowItem = (pkg) => {
    if (onShowItem) {
      onShowItem(pkg);
      onClose();
    }
  };

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 z-[9999] bg-gray-50 overflow-y-auto">
      {/* ===== HEADER ===== */}
      <div className="sticky top-0 z-10 bg-white border-b border-gray-200">
        <div className="px-4 sm:px-6 lg:px-8">
          <div className="flex items-center h-16">
            {/* Back Button */}
            <button
              onClick={onClose}
              className="flex items-center gap-2 text-gray-600 hover:text-gray-900 transition-colors font-medium"
            >
              <FaArrowLeft className="text-lg" />
              <span className="hidden sm:inline">Back</span>
            </button>

            {/* Title */}
            <div className="flex-1 text-center">
              <h1 className="text-lg font-semibold text-gray-900">
                {title}
              </h1>
              <p className="text-xs text-gray-500 hidden sm:block">
                {filteredPackages.length} packages available
              </p>
            </div>

            {/* Spacer for balance */}
            <div className="w-20 sm:w-32"></div>
          </div>
        </div>
      </div>

      {/* ===== TOOLBAR ===== */}
      <div className="sticky top-16 z-10 bg-white/95 backdrop-blur-sm border-b border-gray-200 py-3">
        <div className="px-4 sm:px-6 lg:px-8">
          <div className="flex flex-wrap items-center gap-3">
            {/* Search */}
            <div className="relative flex-1 min-w-[200px]">
              <FaSearch className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400 text-sm" />
              <input
                type="text"
                placeholder="Search packages..."
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                className="w-full pl-9 pr-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent text-sm bg-gray-50 hover:bg-white transition-colors"
              />
            </div>

            {/* Filter */}
            <select
              value={filterBy}
              onChange={(e) => setFilterBy(e.target.value)}
              className="px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent text-sm bg-gray-50 hover:bg-white transition-colors cursor-pointer"
            >
              {categories.map(cat => (
                <option key={cat} value={cat}>
                  {cat === 'all' ? 'All Categories' : cat}
                </option>
              ))}
            </select>

            {/* Sort */}
            <select
              value={sortBy}
              onChange={(e) => setSortBy(e.target.value)}
              className="px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent text-sm bg-gray-50 hover:bg-white transition-colors cursor-pointer"
            >
              <option value="default">Sort</option>
              <option value="price-low">Price: Low → High</option>
              <option value="price-high">Price: High → Low</option>
              <option value="rating">Rating</option>
              <option value="name">Name</option>
            </select>

            {/* View Mode */}
            <div className="flex border border-gray-300 rounded-lg overflow-hidden bg-white">
              <button
                onClick={() => setViewMode('grid')}
                className={`px-3 py-2 transition-colors ${
                  viewMode === 'grid'
                    ? 'bg-blue-600 text-white'
                    : 'text-gray-600 hover:bg-gray-100'
                }`}
                title="Grid View"
              >
                <FaThLarge className="text-sm" />
              </button>
              <button
                onClick={() => setViewMode('list')}
                className={`px-3 py-2 transition-colors border-l border-gray-300 ${
                  viewMode === 'list'
                    ? 'bg-blue-600 text-white'
                    : 'text-gray-600 hover:bg-gray-100'
                }`}
                title="List View"
              >
                <FaList className="text-sm" />
              </button>
            </div>

            {/* Results count */}
            <span className="text-sm text-gray-500 ml-auto hidden lg:block">
              {filteredPackages.length} results
            </span>
          </div>
        </div>
      </div>

      {/* ===== PACKAGES GRID ===== */}
      <div className="px-4 sm:px-6 lg:px-8 py-6">
        {filteredPackages.length === 0 ? (
          <div className="text-center py-20">
            <div className="text-6xl mb-4">🔍</div>
            <h3 className="text-2xl font-semibold text-gray-800 mb-2">
              No packages found
            </h3>
            <p className="text-gray-500">
              Try adjusting your search or filter criteria
            </p>
            <button
              onClick={() => {
                setSearchTerm('');
                setFilterBy('all');
                setSortBy('default');
              }}
              className="mt-4 px-6 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors"
            >
              Reset Filters
            </button>
          </div>
        ) : (
          <>
            {/* Results Summary */}
            <div className="flex items-center justify-between mb-4 text-sm text-gray-500">
              <span>
                Showing {((currentPage - 1) * itemsPerPage) + 1} -{' '}
                {Math.min(currentPage * itemsPerPage, filteredPackages.length)} of{' '}
                {filteredPackages.length}
              </span>
              {searchTerm && (
                <button
                  onClick={() => setSearchTerm('')}
                  className="text-blue-600 hover:text-blue-800"
                >
                  Clear search
                </button>
              )}
            </div>

            {/* Grid/List */}
            <div className={
              viewMode === 'grid'
                ? 'grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-6'
                : 'space-y-4'
            }>
              {paginatedPackages.map((pkg, index) => (
                viewMode === 'grid' ? (
                  <PackageCard
                    key={index}
                    pkg={pkg}
                    index={index}
                    onShowItem={handleShowItem}
                  />
                ) : (
                  <PackageListItem
                    key={index}
                    pkg={pkg}
                    onShowItem={handleShowItem}
                  />
                )
              ))}
            </div>

            {/* Pagination */}
            {filteredPackages.length > itemsPerPage && (
              <div className="flex items-center justify-center gap-2 mt-8">
                <button
                  onClick={() => handlePageChange(currentPage - 1)}
                  disabled={currentPage === 1}
                  className={`p-2 rounded-lg transition-colors ${
                    currentPage === 1
                      ? 'text-gray-300 cursor-not-allowed'
                      : 'text-gray-600 hover:bg-gray-200'
                  }`}
                >
                  <FaChevronLeft />
                </button>

                {Array.from({ length: Math.min(totalPages, 5) }, (_, i) => {
                  let pageNum;
                  if (totalPages <= 5) {
                    pageNum = i + 1;
                  } else if (currentPage <= 3) {
                    pageNum = i + 1;
                  } else if (currentPage >= totalPages - 2) {
                    pageNum = totalPages - 4 + i;
                  } else {
                    pageNum = currentPage - 2 + i;
                  }

                  return (
                    <button
                      key={i}
                      onClick={() => handlePageChange(pageNum)}
                      className={`w-10 h-10 rounded-lg transition-colors ${
                        currentPage === pageNum
                          ? 'bg-blue-600 text-white'
                          : 'text-gray-600 hover:bg-gray-200'
                      }`}
                    >
                      {pageNum}
                    </button>
                  );
                })}

                {totalPages > 5 && currentPage < totalPages - 2 && (
                  <>
                    <span className="text-gray-400">...</span>
                    <button
                      onClick={() => handlePageChange(totalPages)}
                      className="w-10 h-10 rounded-lg text-gray-600 hover:bg-gray-200 transition-colors"
                    >
                      {totalPages}
                    </button>
                  </>
                )}

                <button
                  onClick={() => handlePageChange(currentPage + 1)}
                  disabled={currentPage === totalPages}
                  className={`p-2 rounded-lg transition-colors ${
                    currentPage === totalPages
                      ? 'text-gray-300 cursor-not-allowed'
                      : 'text-gray-600 hover:bg-gray-200'
                  }`}
                >
                  <FaChevronRightIcon />
                </button>
              </div>
            )}
          </>
        )}
      </div>
    </div>
  );
};

// List View Item Component
const PackageListItem = ({ pkg, onShowItem }) => {
  return (
    <div className="bg-white rounded-2xl shadow-lg hover:shadow-2xl transition-all duration-300 overflow-hidden flex flex-col md:flex-row">
      <div className="relative md:w-48 h-48 md:h-auto flex-shrink-0">
        <img
          src={pkg.bgImage || pkg.image}
          alt={pkg.name}
          className="w-full h-full object-cover"
        />
        {pkg.rating && (
          <div className="absolute top-3 right-3 bg-white/90 backdrop-blur-sm px-2 py-1 rounded-full flex items-center gap-1">
            <FaStar className="text-yellow-400 text-xs" />
            <span className="text-xs font-semibold text-gray-700">{pkg.rating}</span>
          </div>
        )}
      </div>
      
      <div className="flex-1 p-6 flex flex-col">
        <div className="flex-1">
          <h3 className="text-xl font-bold text-gray-800 mb-2">
            {pkg.label || pkg.name}
          </h3>
          <p className="text-gray-500 text-sm line-clamp-2 mb-3">
            {pkg.description}
          </p>
          <div className="flex flex-wrap gap-2">
            {pkg.details?.slice(0, 3).map((detail, i) => (
              <span key={i} className="text-xs bg-gray-100 px-3 py-1 rounded-full text-gray-600">
                {detail.value}
              </span>
            ))}
          </div>
        </div>
        
        <div className="flex items-center justify-between mt-4 pt-4 border-t border-gray-100">
          <div className="flex gap-3">
            {pkg.onImageButtons?.slice(0, 2).map((btn, i) => (
              <span key={i} className="text-sm font-semibold text-gray-800">
                {btn.label}: {btn.value}
              </span>
            ))}
          </div>
          <button
            onClick={() => onShowItem(pkg)}
            className="px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors text-sm font-semibold"
          >
            View Details
          </button>
        </div>
      </div>
    </div>
  );
};

export default FullScreenPackages;